import subtractDays from 'date-fns/sub_days';
import differenceInCalendarDays from 'date-fns/difference_in_calendar_days';

import {
  FETCH_EXPENSES,
  FETCH_EXPENSES_SUCCESS,
  FETCH_EXPENSES_ERROR,
  FETCH_EXPENSES_SERVER_ERROR,
  FETCH_PAYMENTS,
  FETCH_PAYMENTS_SUCCESS,
  FETCH_PAYMENTS_ERROR,
  UPDATE_PERIOD_START,
  UPDATE_PERIOD_END,
  FETCH_EMAIL,
  FETCH_EMAIL_ERROR,
  FETCH_EMAIL_SUCCESS,
  MAX_REPORT_PERIOD_IN_DAYS,
  TOGGLE_EXPENSES_FILTER,
  TOGGLE_ONLY_PAID_FILTER,
  FETCH_POSTPAID_INVOICES,
  FETCH_POSTPAID_INVOICES_SUCCESS,
  FETCH_POSTPAID_INVOICES_ERROR,
  EXPENSES_SECTIONS,
  SEND_INVOICE_TO_EMAIL,
  SEND_INVOICE_TO_EMAIL_SUCCESS,
  SEND_INVOICE_TO_EMAIL_ERROR,
  FETCH_INVOICE_DETAILS,
  FETCH_INVOICE_DETAILS_SUCCESS,
  FETCH_INVOICE_DETAILS_ERROR,
  CHECK_EMAIL_NOTIFICATIONS,
  CHECK_EMAIL_NOTIFICATIONS_SUCCESS,
  CHECK_EMAIL_NOTIFICATIONS_ERROR,
  SAVE_DETALIZATION,
  SAVE_DETALIZATION_SUCCESS,
  SAVE_DETALIZATION_ERROR,
  TOGGLE_PAYMENTS_DETAILS_ROW,
  FETCH_INTERNET_STATS,
  FETCH_INTERNET_STATS_SUCCESS,
  FETCH_INTERNET_STATS_ERROR,
  FETCH_CASHFLOW_STATS,
  FETCH_CASHFLOW_STATS_SUCCESS,
  FETCH_CASHFLOW_STATS_ERROR,
  UPDATE_INTERNET_PERIOD_START,
  UPDATE_INTERNET_PERIOD_END,
  MAX_INTERNET_REPORT_PERIOD_IN_DAYS,
} from './constants';

const initialState = {
  expenses: {
    isFetching: true,
    isReportSending: false,
    reportSendingError: null,
  },
  payments: {
    isFetching: false,
    isEmpty: false,
  },
  email: {
    isFetching: false,
    isExist: false,
    isConfirmed: false,
    isChecking: false,
    checkingError: null,
    emailAddress: null,
  },
  period: {
    start: new Date(),
    end: new Date(),
    isDirty: false,
    isValid: true,
  },
  internetPeriod: {
    start: subtractDays(new Date(), 14),
    end: new Date(),
    isDirty: false,
    isValid: true,
  },
  internetStats: {
    isFetching: false,
    error: null,
  },
  cashflowStats: {
    isFetching: false,
    error: null,
  },
  invoices: {
    isEmpty: false,
    currentPeriod: null,
    invoicePeriods: null,
    isFetching: true,
    isSending: false,
    sendingError: null,
    error: null,
  },
};

const prepareFilters = (filters) => {
  if (!filters) {
    return [];
  }

  const newFilters = filters.map((filter) => ({
    ...filter,
    activeIn: {
      [EXPENSES_SECTIONS.STRUCTURE]: true,
      [EXPENSES_SECTIONS.DETALIZATION]: true,
    },
    visibleIn: {
      [EXPENSES_SECTIONS.STRUCTURE]: filter.isShowInStructure,
      [EXPENSES_SECTIONS.DETALIZATION]: filter.isShowInDetalization,
    },
  }));

  newFilters.forEach(({ parentFilterId, id: childId }, index) => {
    if (parentFilterId) {
      const parentFilter = newFilters.find(({ id }) => id === parentFilterId);

      if (parentFilter) {
        parentFilter.childrenIds = [...parentFilter.childrenIds || [], childId];
        if (!parentFilter.trafficVolume && !parentFilter.cost) {
          newFilters[index].hidden = true;
        }
      }
    }
  });

  return newFilters;
};

const prepareCategories = (filters) => {
  if (!filters) {
    return [];
  }

  const nestingLevel = (parentFilterId) => {
    const parent = filters.find(({ id }) => id === parentFilterId);
    if (!parent) return 'Zero';
    return parent.parentFilterId ? 'Two' : 'One';
  };

  const categories = filters
    .filter(({ isShowInStructure }) => isShowInStructure)
    .map(({
      id, name, parentFilterId, cost, trafficVolume, trafficUnit,
    }) => {
      const parentFilter = filters.find((item) => item.id === parentFilterId);

      return {
        name,
        nested: !!parentFilterId,
        nestingLevel: nestingLevel(parentFilterId),
        filterId: id,
        volume: trafficVolume,
        unit: trafficUnit,
        sum: cost,
        hidden: parentFilter && !parentFilter.cost && !parentFilter.trafficVolume || false,
      };
    });

  return categories;
};

const isPeriodValid = (startDate, endDate) => differenceInCalendarDays(endDate, startDate) <= MAX_REPORT_PERIOD_IN_DAYS;

const isInternetPeriodValid = (startDate, endDate) => differenceInCalendarDays(endDate, startDate)
  <= MAX_INTERNET_REPORT_PERIOD_IN_DAYS;

const allSiblingsAreUnchecked = (
  filters,
  currentFilter,
  section = EXPENSES_SECTIONS.STRUCTURE,
) => filters
  .filter((filter) => filter.parentFilterId === currentFilter.parentFilterId && filter !== currentFilter)
  .every(({ activeIn }) => !activeIn[section]);

const toggleFilter = (filters, {
  filterId,
  checked,
  ignoreParent = false,
  ignoreChildren = false,
  section = EXPENSES_SECTIONS.STRUCTURE,
}) => {
  const filter = filters
    .find(({ id }) => id === filterId);

  if (filter && filter.activeIn[section] !== checked) {
    let newFilters = filters.map((category) => (category === filter ? ({
      ...category,
      activeIn: {
        ...category.activeIn,
        [section]: checked,
      },
    }) : category));

    if (!ignoreChildren && filter.children) {
      newFilters = filter.childrenIds
        .reduce((acc, childId) => toggleFilter(acc, {
          checked,
          section,
          ignoreParent: true,
          filterId: childId,
        }), newFilters);
    }

    if (!ignoreParent && allSiblingsAreUnchecked(filters, filter, section)) {
      newFilters = toggleFilter(newFilters, {
        checked,
        section,
        ignoreChildren: true,
        filterId: filter.parentFilterId,
      });
    }

    return newFilters;
  }

  return filters;
};

const flattenBens = (currentPeriod) => {
  if (currentPeriod && currentPeriod.bens) {
    return {
      ...currentPeriod,
      bens: currentPeriod.bens
        .reduce((acc, { subscribers, ...rest }) => [...acc, ...subscribers.map((subscriber) => ({
          ...subscriber,
          ...rest,
        }))], []),
    };
  }

  return currentPeriod;
};

const prepareDetails = (details = []) => (
  details.reduce((acc, { ctn, transactions }) => ([
    ...acc,
    ...transactions.map((item) => ({ ...item, ctn })),
  ]), [])
);

const initInvoiceDetails = (state, data) => {
  const transactions = prepareDetails(data.ctnDetails);

  return {
    ...data,
    transactions,
    ctnDetails: null,
    structure: prepareCategories(data.filters),
    filters: prepareFilters(data.filters),
  };
};

const initPayments = (payments) => payments.map((payment, index) => ({
  ...payment,
  id: index,
}));

const togglePaymentsDetailsRow = (payments, { id, isExpanded }) => payments.map((payment) => (payment.id === id ? ({
  ...payment,
  expanded: isExpanded,
}) : payment));

const updateEmail = (currentPeriod, { email, benNumber }) => ({
  ...currentPeriod,
  bens: currentPeriod.bens
    .map((ben) => (ben.benNumber === benNumber ? {
      ...ben,
      email,
    } : ben)),
});

export default function app(state = initialState, action) {
  switch (action.type) {
    case FETCH_EXPENSES:
      return {
        ...state,
        expenses: {
          ...state.expenses,
          isFetching: true,
        },
        period: {
          ...state.period,
          isDirty: false,
        },
      };

    case FETCH_EXPENSES_SUCCESS:
      return {
        ...state,
        expenses: {
          isFetching: false,
          error: null,
          ...action.data,
          structure: prepareCategories(action.data.filters),
          filters: prepareFilters(action.data.filters),
        },
      };

    case FETCH_EXPENSES_ERROR:
      return {
        ...state,
        expenses: {
          ...state.expenses,
          isFetching: false,
          error: action.error,
        },
      };

    case FETCH_EXPENSES_SERVER_ERROR:
      return {
        ...state,
        expenses: {
          ...state.expenses,
          isFetching: false,
          error: action.error,
        },
        period: {
          ...state.period,
          isDirty: true,
        },
      };

    case FETCH_PAYMENTS:
      return {
        ...state,
        payments: {
          ...state.payments,
          isFetching: true,
        },
      };

    case FETCH_PAYMENTS_SUCCESS:
      return {
        ...state,
        payments: {
          ...state.payments,
          list: action.data && initPayments(action.data),
          isEmpty: !action.data,
          isFetching: false,
          error: null,
        },
      };

    case FETCH_PAYMENTS_ERROR:
      return {
        ...state,
        payments: {
          ...state.payments,
          isFetching: false,
          error: action.error,
        },
      };

    case UPDATE_PERIOD_START:
      return {
        ...state,
        period: {
          ...state.period,
          start: action.date,
          isDirty: true,
          isValid: isPeriodValid(action.date, state.period.end),
        },
      };

    case UPDATE_PERIOD_END:
      return {
        ...state,
        period: {
          ...state.period,
          end: action.date,
          isDirty: true,
          isValid: isPeriodValid(state.period.start, action.date),
        },
      };

    case UPDATE_INTERNET_PERIOD_START:
      return {
        ...state,
        internetPeriod: {
          ...state.internetPeriod,
          start: action.date,
          isDirty: true,
          isValid: isInternetPeriodValid(action.date, state.internetPeriod.end),
        },
      };

    case UPDATE_INTERNET_PERIOD_END:
      return {
        ...state,
        internetPeriod: {
          ...state.internetPeriod,
          end: action.date,
          isDirty: true,
          isValid: isInternetPeriodValid(state.internetPeriod.start, action.date),
        },
      };

    case FETCH_EMAIL:
      return {
        ...state,
        email: {
          ...state.email,
          isFetching: true,
        },
      };

    case FETCH_EMAIL_SUCCESS:
      return {
        ...state,
        email: {
          ...state.email,
          isFetching: false,
          isExist: action.emailInfo.isEmailExist,
          emailAddress: action.emailInfo.emailAddress,
        },
      };

    case FETCH_EMAIL_ERROR:
      return {
        ...state,
        email: {
          ...state.email,
          isFetching: false,
          error: action.error,
        },
      };

    case FETCH_INTERNET_STATS:
      return {
        ...state,
        internetStats: {
          ...state.internetStats,
          isFetching: true,
          isArchiveAvailable: false,
        },
        internetPeriod: {
          ...state.internetPeriod,
          isDirty: false,
        },
      };

    case FETCH_INTERNET_STATS_SUCCESS:
      return {
        ...state,
        internetStats: {
          ...state.internetStats,
          isFetching: false,
          data: action.data.internetStatsRows,
          isArchiveAvailable: action.data.isArchiveAvailable,
          error: null,
        },
      };

    case FETCH_INTERNET_STATS_ERROR:
      return {
        ...state,
        internetStats: {
          ...state.internetStats,
          isFetching: false,
          data: null,
          error: action.error,
        },
      };

    case FETCH_CASHFLOW_STATS:
      return {
        ...state,
        cashflowStats: {
          ...state.cashflowStats,
          isFetching: true,
        },
      };

    case FETCH_CASHFLOW_STATS_SUCCESS:
      return {
        ...state,
        cashflowStats: {
          ...state.cashflowStats,
          isFetching: false,
          data: action.data.cashflowRequestsList,
          error: null,
        },
      };

    case FETCH_CASHFLOW_STATS_ERROR:
      return {
        ...state,
        cashflowStats: {
          ...state.cashflowStats,
          isFetching: false,
          data: null,
          error: action.error,
        },
      };

    case TOGGLE_EXPENSES_FILTER:
      return {
        ...state,
        expenses: {
          ...state.expenses,
          filters: toggleFilter(state.expenses.filters, action.data),
        },
      };

    case TOGGLE_ONLY_PAID_FILTER:
      return {
        ...state,
        expenses: {
          ...state.expenses,
          onlyFreeActions: !state.expenses.onlyFreeActions,
        },
      };

    case FETCH_POSTPAID_INVOICES:
      return {
        ...state,
        invoices: {
          ...state.invoices,
          isFetching: true,
        },
      };

    case FETCH_POSTPAID_INVOICES_SUCCESS:
      return {
        ...state,
        invoices: {
          ...state.invoices,
          ...action.data,
          isEmpty: !action.data,
          currentPeriod: action.data && flattenBens(action.data.currentPeriod),
          isFetching: false,
          error: null,
        },
      };

    case FETCH_POSTPAID_INVOICES_ERROR:
      return {
        ...state,
        invoices: {
          ...state.invoices,
          isFetching: false,
          currentPeriod: null,
          invoicePeriods: null,
          error: action.error,
        },
      };

    case SEND_INVOICE_TO_EMAIL:
      return {
        ...state,
        invoices: {
          ...state.invoices,
          isSending: true,
        },
      };

    case SEND_INVOICE_TO_EMAIL_SUCCESS:
      return {
        ...state,
        invoices: {
          ...state.invoices,
          currentPeriod: updateEmail(state.invoices.currentPeriod, action.data),
          isSending: false,
          sendingError: null,
        },
      };

    case SEND_INVOICE_TO_EMAIL_ERROR:
      return {
        ...state,
        invoices: {
          ...state.invoices,
          isSending: false,
          sendingError: action.error,
        },
      };

    case FETCH_INVOICE_DETAILS:
      return {
        ...state,
        expenses: {
          ...state.expenses,
          isFetching: true,
        },
      };

    case FETCH_INVOICE_DETAILS_SUCCESS:
      return {
        ...state,
        expenses: initInvoiceDetails({
          isFetching: false,
          error: null,
        }, action.data),
      };

    case FETCH_INVOICE_DETAILS_ERROR:
      return {
        ...state,
        expenses: {
          ...state.expenses,
          isFetching: false,
          error: action.error,
        },
      };

    case CHECK_EMAIL_NOTIFICATIONS:
      return {
        ...state,
        email: {
          ...state.email,
          isChecking: true,
        },
      };

    case CHECK_EMAIL_NOTIFICATIONS_SUCCESS:
      return {
        ...state,
        email: {
          ...state.email,
          isConfirmed: action.data,
          isChecking: false,
          checkingError: null,
        },
      };

    case CHECK_EMAIL_NOTIFICATIONS_ERROR:
      return {
        ...state,
        email: {
          ...state.email,
          isConfirmed: false,
          isChecking: false,
          checkingError: action.error,
        },
      };

    case SAVE_DETALIZATION:
      return {
        ...state,
        expenses: {
          ...state.expenses,
          isReportSending: true,
        },
      };

    case SAVE_DETALIZATION_SUCCESS:
      return {
        ...state,
        expenses: {
          ...state.expenses,
          isReportSending: false,
          reportSendingError: null,
        },
      };

    case SAVE_DETALIZATION_ERROR:
      return {
        ...state,
        expenses: {
          ...state.expenses,
          isReportSending: false,
          reportSendingError: action.error,
        },
      };

    case TOGGLE_PAYMENTS_DETAILS_ROW:
      return {
        ...state,
        payments: {
          ...state.payments,
          list: togglePaymentsDetailsRow(state.payments.list, action.data),
        },
      };

    default:
      return state;
  }
}
